import { useCallback, useMemo, useState, useContext } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid2,
    Stack,
    Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import Switch from '../../../Shared/Components/Switch';
import ListViewTableHeaderRow from '../../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import useGetInvoices from '../../hooks/useGetInvoices';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import { COLORS } from '../../../utils/Application_Constants';
import BookingContainerCollapse from './InvoiceInitiationTable/BookingContainerCollapse';
import { InvoicingSelectionContextProvider } from '../../Contexts/InvoicingSelectionContext';
import useInvoicingFilters from '../../hooks/Invoicing/useFilters';
import FilterDrawer from '../../../Shared/Components/FilterDrawer/FilterDrawer';
import SearchInputWithDropdown from '../../../Shared/Components/SearchInputWithDropdown';
import { initiationSearchOptions } from '../../Configurations/invoicingConfig';
import { pluralize } from '../../../utils/helpers';
import { formatFiltersForApi } from '../../../utils/filterUtils';
import InvoiceInitiationConfirmationModal from './InvoiceInitiationConfirmationModal';
import PaginationBar from '../../../Shared/Components/PaginationBar';
import { AdminAuthContext } from '../../Contexts/AdminAuthContext';

const tableHeaders = [
    { label: 'Client', key: 'client', size: 1 },
    { label: 'Clinician', key: 'clinician', size: 1 },
    { label: 'Bookings', key: 'booking', size: 1 },
    { label: '# of Location', key: 'numberOfLocation', size: 1 },
    { label: 'Frequency', key: 'frequency', size: 1 },
    { label: 'Collector', key: 'collector', size: 1 },
    { label: 'Types', key: 'types', size: 1 },
];

const pageSize = 20;

const InitiationTable = ({ pageType = 'invoiceInitiation' }) => {
    const { location, replace, push } = useHistory();
    const [doNotSendToClient, setDoNotSendToClient] = useState(true);
    const [successState, setSuccessState] = useState({ showAlert: false, count: 0, action: '' });
    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(`${pageType}SortField`);
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${pageType}SortDirection`,
        'asc'
    );
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${pageType}Search`, {
        searchWord: '',
        searchColumns: '',
    });

    const { isInvoicingSystemAdminUser } = useContext(AdminAuthContext);

    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useInvoicingFilters(pageType);

    const [page, setPage] = useState(() => {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.has('page') && parseInt(urlParams.get('page'), 10) > 0) {
            return parseInt(urlParams.get('page'), 10);
        }
        replace(`${location.pathname}?page=1`);
        return 1;
    });
    const handlePageChange = (_, newPage) => {
        push(`${location.pathname}?page=${newPage}`);
        setPage(newPage);
    };

    const requestParams = useMemo(
        () => ({
            offset: (page - 1) * pageSize,
            pageSize,
            ...formatFiltersForApi(filters, filtersSetup),
            ...(sortBy ? { orderBy: sortBy, dir: sortDirection } : {}),
            ...(searchData?.searchWord ? searchData : {}),
        }),
        [page, sortBy, sortDirection, filters, filtersSetup, searchData, pageSize]
    );

    const {
        data: bookings,
        isLoading,
        refetch: refetchGetInvoices,
    } = useGetInvoices(requestParams);
    const [showInvoiceInitiationConfirmationModal, setShowInvoiceInitiationConfirmationModal] =
        useState(false);
    const [invoiceAction, setInvoiceAction] = useState('invoice'); // 'invoice' or 'pre-invoice'
    const totalCount = bookings?.totalCount || 0;

    const handlePreInvoiceClick = () => {
        setInvoiceAction('pre-invoice');
        setShowInvoiceInitiationConfirmationModal(true);
    };

    const handleInvoiceClick = () => {
        setInvoiceAction('invoice');
        setShowInvoiceInitiationConfirmationModal(true);
    };

    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
    }, []);

    const renderPagination = (position) => {
        if (isLoading || !totalCount) return null;
        return (
            <Box
                sx={{ mt: position === 'top' ? 0 : '15px', mb: position === 'bottom' ? 0 : '15px' }}
            >
                <PaginationBar
                    page={page}
                    totalPages={Math.ceil(totalCount / pageSize)}
                    handlePageChange={handlePageChange}
                />
            </Box>
        );
    };

    const bookingItems = useMemo(
        () =>
            bookings?.data?.map((booking) => ({
                ...booking,
                expenses:
                    booking?.expenses?.items?.length > 0 // filter out expenses with no items
                        ? [
                              {
                                  id: booking.expenses.items?.map((item) => item.id)?.join(''), // generate id for expense items wrapped based on expenses items ids
                                  ...booking.expenses,
                              },
                          ]
                        : [],
            })) || [],
        [bookings]
    );

    return (
        <>
            <Grid2 container alignItems="center" spacing={1} mt="12px">
                <FilterDrawer
                    isLoading={isLoadingFilterConfig || isLoading}
                    config={filtersSetup}
                    onChange={handleApplyFilter}
                    storedValues={filters}
                />
                <SearchInputWithDropdown
                    isLoading={isLoading}
                    options={initiationSearchOptions}
                    initialSearch={searchData?.searchWord}
                    initialSearchKey={searchData?.searchColumns}
                    onSubmit={({ search, searchKey }) =>
                        setSearch({ searchWord: search, searchColumns: searchKey })
                    }
                    sx={{ ml: 'auto' }}
                />
            </Grid2>

            <Typography sx={{ my: 2, fontSize: 12, fontWeight: '400', color: COLORS.LT_MIDNIGHT }}>
                {`${totalCount} ${pluralize(totalCount, 'item')}`}
                {appliedFiltersDescription}
            </Typography>

            {successState.showAlert && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setSuccessState({ showAlert: false, count: 0, action: '' })}
                >
                    <Typography variant="body2" sx={{ mt: '1px' }}>
                        {successState.count} items have been queued for {successState.action}. The
                        process is running in the background. If you don’t see the{' '}
                        {successState.action} in this list, try refreshing.
                    </Typography>
                </Alert>
            )}

            {renderPagination('top')}

            <InvoicingSelectionContextProvider
                bookings={bookingItems || []}
                totalCount={totalCount}
            >
                {({ selectStatus, handleSelectAllBookings, selectedItemsCount }) => (
                    <>
                        <Box
                            sx={{
                                py: '22px',
                                px: '26px',
                                backgroundColor: COLORS.WHITE,
                                borderRadius: '10px',
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                mb="20px"
                            >
                                <Typography>Unbill items</Typography>
                                {isInvoicingSystemAdminUser && (
                                    <>
                                        <FormControlLabel
                                            sx={{
                                                ml: 'auto',
                                                '& .MuiFormControlLabel-label': {
                                                    color: COLORS.LT_MIDNIGHT,
                                                    fontSize: 14,
                                                    fontStyle: 'normal',
                                                    fontWeight: 400,
                                                    lineHeight: '16.5px',
                                                },
                                            }}
                                            checked={doNotSendToClient}
                                            onChange={(e) => setDoNotSendToClient(e.target.checked)}
                                            control={<Switch />}
                                            label="Do not sent to client"
                                        />
                                        <Stack direction="row" alignItems="center" gap="10px">
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                disabled={selectedItemsCount === 0}
                                                onClick={handlePreInvoiceClick}
                                            >
                                                Pre-invoice ({selectedItemsCount})
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                disabled={selectedItemsCount === 0}
                                                onClick={handleInvoiceClick}
                                            >
                                                Invoice Selected ({selectedItemsCount})
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                            </Stack>
                            {bookingItems.length > 0 ? (
                                <>
                                    <ListViewTableHeaderRow
                                        selectStatus={selectStatus}
                                        onSelectAll={handleSelectAllBookings}
                                        selectColumnProps={{
                                            sx: {
                                                justifyContent: 'flex-end',
                                                width: '90px',
                                                pr: '2.5px !important',
                                            },
                                        }}
                                        columns={tableHeaders}
                                        onSortChange={handleSortChange}
                                        sortBy={sortBy}
                                        sortDirection={sortDirection}
                                        shouldCleanSort
                                        sortIconPosition="left"
                                    />

                                    {bookingItems.map((booking) => (
                                        <BookingContainerCollapse
                                            key={booking.bookingId}
                                            booking={booking}
                                        />
                                    ))}
                                </>
                            ) : (
                                <div>{isLoading ? <CircularProgress /> : 'No invoices found'}</div>
                            )}
                        </Box>
                        <InvoiceInitiationConfirmationModal
                            open={showInvoiceInitiationConfirmationModal}
                            onClose={() => setShowInvoiceInitiationConfirmationModal(false)}
                            action={invoiceAction}
                            doNotSendToClient={doNotSendToClient}
                            setDoNotSendToClient={setDoNotSendToClient}
                            setSuccessState={setSuccessState}
                            refetch={refetchGetInvoices}
                        />
                    </>
                )}
            </InvoicingSelectionContextProvider>

            {renderPagination('bottom')}
        </>
    );
};

export default InitiationTable;
